// @givmo/employer-module — EVERY word the Security panels render, in ONE place (AS-WEB8).
//
// ══ ONE PLACE, SO A VARIANT IS DATA ═══════════════════════════════════════════════════════════
// The panels render on the employer portal's Settings › Security tab and on the company console's
// Admin Console › Security page, and they speak to a company ("company Owners", "employees",
// "linked their work email") because a company is who reads them. The charity console reserves a
// Security page too, structurally unrevealed today: no charity-class auth-policy or SSO surface
// exists. If one is ever planned, the panels' words need organization-aware variants — so every
// string they render lives HERE and the panels spell none of their own: a variant is a second set of
// words beside these, never a rewrite of the panels.
//
// ══ TWO KINDS OF STRING, EACH MARKED ══════════════════════════════════════════════════════════
//   • APPROVED — Jonathan's signed words: an object with no suffix. A change is a RE-approval, and
//     `security-copy-freeze.test.tsx` pins each byte for byte.
//   • DRAFT — words waiting for his sweep: the `_DRAFT` suffix, under a `THIS COPY IS DRAFT` header.
//     A change IS the approval; the draft freeze tests pin them so the sweep is a deliberate edit.
//
// Sections: the two-factor tab · the counts and units · the company two-factor requirement · company
// sign-in.

import type { ComplianceSummary, MfaComplianceStatus, MfaPolicyTier } from './security-decode';

// ── The two-factor tab (APPROVED) ─────────────────────────────────────────────────────────────────
// The APPROVED employer-facing copy (FROZEN — Jonathan-signed 2026-08-12, byte-for-byte; its eight
// "two-factor sign-in" strings re-approved as "two-factor authentication" by D-AS-39, 2026-10-05).
// Every string below is POLICY copy: it states what a company is asking of its employees and what
// happens if they do not. Changing one is a re-approval, not an edit — the freeze test
// (`security-copy-freeze.test.tsx`) pins each string exactly, so a reword fails loudly rather than
// shipping.
//
// Two constraints the wording carries, and the reasons it needed sign-off at all:
//
//   • D-AS-11 — an unmet `require` withholds TRANSACTIONAL BENEFITS ONLY (match requests, steps
//     competitions, employer gifts). It must never say or imply that an account is locked or that
//     company content stops flowing; content withholding is the L3/L4 SSO product and does not
//     exist at this tier.
//   • HONEST CLAIMS — the enforcement ceiling is not on this wire (see the file header), so no
//     string may assert that enforcement is live or promise when it will be. `PANEL_STORED_REQUIRE_
//     NOT_LIVE` is the line that gets closest: it states two facts the roster actually evidences
//     and deliberately promises no timing.

const INTRO =
  'Choose what you ask of employees who have linked their work email to Givmo. This is your ' +
  'company’s setting — it applies to employees, never to your admin sign-in.';

export interface TierCopy {
  tier: MfaPolicyTier;
  label: string;
  description: string;
}

const TIER_COPY: readonly TierCopy[] = [
  {
    tier: 'none',
    label: 'Not asked',
    description: 'Employees are not asked to set up two-factor authentication.',
  },
  {
    tier: 'encourage',
    label: 'Encouraged',
    description:
      'Employees are asked to set up two-factor authentication. Nothing is withheld if they don’t.',
  },
  {
    tier: 'require',
    label: 'Required',
    description:
      'Employees are asked to set up two-factor authentication. Once an employee’s grace period ' +
      'has run out, they keep their account and everything your company shares with them, but ' +
      'company benefits — match requests, steps competitions and employer gifts — pause until they ' +
      'set it up.',
  },
];

const CARD_TITLE = 'Two-factor authentication';
const TIER_GROUP_LABEL = 'Two-factor authentication requirement';
/** Follows the percentage. "you are asking" is load-bearing — the denominator is the ASKED, not all. */
const SHARE_SUFFIX = 'of the employees you are asking have set up two-factor authentication.';

const GRACE_LABEL = 'Grace period';
const GRACE_NOTE =
  'An employee’s grace period starts the first time they open Givmo after you set this, and nothing ' +
  'is withheld until it runs out. Setting 0 days means benefits pause as soon as they open the app.';

const SAVE_LABEL = 'Save';
const SAVED_TOAST = 'Your security setting is saved';
const SAVE_FAILED = 'Could not save your security setting.';
const FORBIDDEN_NOTE = 'Only company Owners can change this setting.';
const DARK_NOTE = 'Employee security settings are not available on this environment yet.';

const PANEL_TITLE = 'Employee two-factor authentication';
const PANEL_SUBTITLE = 'What your employees’ accounts actually show right now.';
/** The lead sentence: it stands alone once Required is saved (D-AS-39 fix 4e, AS-WEB20). */
const PANEL_NOT_ASKED_LEAD =
  'No employee is being asked for two-factor authentication right now, so there is nothing to ' +
  'report here.';
const PANEL_NOT_ASKED = `${PANEL_NOT_ASKED_LEAD} Set the requirement above to Required to start asking.`;
const PANEL_STORED_REQUIRE_NOT_LIVE =
  'Your setting is saved as Required. Employees are not being asked yet — when they are, their ' +
  'status appears here and on the Employees page.';
const PANEL_EMPTY = 'No employees have linked their work email yet.';
const PANEL_UNDISCLOSED = 'Two-factor status is not shown for your account.';
const PANEL_FAILED = 'Could not load employee two-factor status.';
const PANEL_RETRY = 'Try again';

/**
 * Every approved string, in one place, so the freeze test can pin them AT THEIR SOURCE rather than
 * through rendered markup — a pin that survives layout changes and fails only on an actual copy
 * change. The panels render from it (AS-WEB8: one place for every word).
 */
export const SECURITY_COPY = {
  intro: INTRO,
  tiers: TIER_COPY,
  graceLabel: GRACE_LABEL,
  graceNote: GRACE_NOTE,
  save: SAVE_LABEL,
  savedToast: SAVED_TOAST,
  saveFailed: SAVE_FAILED,
  forbidden: FORBIDDEN_NOTE,
  dark: DARK_NOTE,
  panelTitle: PANEL_TITLE,
  panelSubtitle: PANEL_SUBTITLE,
  panelNotAsked: PANEL_NOT_ASKED,
  /** AS-WEB20 (D-AS-39, fix 4e): with Required already saved, the requirement live or dark. */
  panelNotAskedRequireSaved: PANEL_NOT_ASKED_LEAD,
  panelStoredRequireNotLive: PANEL_STORED_REQUIRE_NOT_LIVE,
  panelEmpty: PANEL_EMPTY,
  panelUndisclosed: PANEL_UNDISCLOSED,
  panelFailed: PANEL_FAILED,
  panelRetry: PANEL_RETRY,
  cardTitle: CARD_TITLE,
  tierGroupLabel: TIER_GROUP_LABEL,
  shareSuffix: SHARE_SUFFIX,
} as const;

/** Counting language for the sample. Kept out of the JSX so the whole claim is readable in one place. */
export function countedLabel(summary: ComplianceSummary): string {
  const people = summary.counted === 1 ? '1 employee' : `${summary.counted} employees`;
  return summary.truncated ? `Based on the first ${people}.` : `Based on ${people}.`;
}

// ── The counts and units the panels compose (APPROVED with the tab, 2026-08-12) ───────────────────

/** The panel's three count badges — the status words the roster uses too (`MFA_STATUS_LABELS`). */
export function enrolledBadge(count: number): string {
  return `${count} set up`;
}
export function pendingBadge(count: number): string {
  return `${count} within grace period`;
}
export function overdueBadge(count: number): string {
  return `${count} past grace period`;
}

/**
 * The status words, one per server status — shared by the panel's badges and the roster's column so
 * a status can never be called one thing in Security and another on Employees. `not_required` is a
 * fact about the POLICY (the employer asks nothing), never about the account: "Not asked", never
 * "None", "Off" or "Unprotected".
 */
export const MFA_STATUS_LABELS: Readonly<Record<MfaComplianceStatus, string>> = {
  enrolled: 'Set up',
  pending: 'Within grace period',
  overdue: 'Past grace period',
  not_required: 'Not asked',
};

/**
 * AS-WEB20 (D-AS-39, fix 4): under the company two-factor requirement there is no grace period — an
 * employee without a second factor reads `overdue` at once, and `pending` never occurs — so the panel
 * draws no grace badge, and its red badge and the roster's red word say the fact itself.
 */
export function notSetUpBadge(count: number): string {
  return `${count} not set up`;
}

/** The roster's words under the requirement: "Not set up" for `overdue`, the rest unchanged. */
export const MFA_STATUS_LABELS_UNDER_REQUIREMENT: Readonly<Record<MfaComplianceStatus, string>> = {
  ...MFA_STATUS_LABELS,
  overdue: 'Not set up',
};

/** "1 day" / "14 days" — the grace window's options and company sign-in's re-confirm window. */
export function dayCountLabel(days: number): string {
  return days === 1 ? '1 day' : `${days} days`;
}

/** "1 hour" / "36 hours" — the re-confirm window where it is not whole days. */
export function hourCountLabel(hours: number): string {
  return hours === 1 ? '1 hour' : `${hours} hours`;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// The company two-factor requirement (AS-BE38 (web))
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
// description while the company two-factor requirement is live, and the Owner's confirmation before
// saving Required (plan AS-BE38 (h); Jonathan's ruling on D-AS-16, point 8).
//
// ══ APPROVED — D-AS-39 (copy round 3, Jonathan 2026-10-05) ════════════════════════════════════
// Every string below was a draft until D-AS-39 adopted the round's recommendations: decision 2's
// "two-factor authentication", fix 3's Required text and heads-up timing, and the rest as written.
// A change now is a RE-approval; `security-copy-freeze.test.tsx` pins each byte for byte. The
// approved 2026-08-12 tab strings sit above, unchanged in sense: this object REPLACES the Required
// description only while the requirement is live, and adds the heads-up.
//
// ══ WHAT THE WORDING HAS TO CARRY ═════════════════════════════════════════════════════════════
// Six constraints are load-bearing, and a sweep must keep them, not only the sense:
//
//   1. "WOULD", NEVER "ARE", AND NO "WHEN". The count (AS-BE49) is the requirement's population
//      whatever the stored tier and the enforcement ceiling, because it warns BEFORE Required is
//      saved; the roster's statuses follow the EFFECTIVE tier. Under a clamping
//      `EMPLOYER_AUTH_POLICY_MAX_TIER` the two disagree by design, so the confirmation states what
//      saving WOULD do, never what is happening — and not "the next time they open Givmo", which a
//      ceiling holding the requirement back would make untrue (D-AS-39, fix 3).
//   2. THE PERSONAL ACCOUNT STAYS. Every exit the sheet offers keeps the person's own Givmo account
//      (mission bar §1, D-AS-16). An Owner deciding this needs to know they are not taking anyone's
//      account away, so it is said in both the tier text and the confirmation, in D-AS-30's approved
//      words for the employee ("Your personal account stays yours").
//   3. NO GRACE PERIOD, NO EMAIL. D-AS-16 ruled both out for the requirement. The approved Required
//      text it replaces promised a grace period, and an Owner who set Required before would otherwise
//      expect one.
//   4. ONE NAME EVERYWHERE. "Two-factor authentication", the word the employees' apps, the server's
//      messages and every email use (D-AS-29, D-AS-30), so an Owner and the employees they ask name
//      the same thing (D-AS-39, decision 2).
//   5. WITHHELD MEANS CONTENT AND WHAT THEY ASK FOR, NOT GIFTS. The backstop refuses the company's
//      content and the benefits an employee requests (donation matches, steps competitions); a gift
//      the company sends is delivered whatever their two-factor state (D-AS-12, reaffirmed under
//      D-AS-16: `benefit_surfaces.py` serves occasion gifts, and a card's code still redeems). So the
//      text names the withheld pair in D-AS-30's words and says gifts still arrive.
//   6. THE L4 LOCK EXEMPTS WHOM IT BINDS. An employee the company's sign-in lock binds signs in only
//      through its identity provider and is never asked for a Givmo factor (D-AS-16 (g)), so every
//      line that says who is asked carries `lockExemption` where the lock is on (`tierTiles` and the
//      heads-up say where; the count already leaves those employees out).

const REQUIRED_DESCRIPTION =
  'Employees need two-factor authentication to link their work email and to stay linked. An ' +
  'employee without it is asked to add it or unlink your company the next time they open Givmo, ' +
  'and your company’s content and benefits such as donation matching are withheld from them until ' +
  'they add it and confirm it’s them. Gifts your company sends still reach them. There’s no grace ' +
  'period, and no email is sent. Their personal Givmo account stays theirs.';

export const TWO_FACTOR_REQUIREMENT_COPY = {
  /**
   * The Required tile's description while the requirement is live (the served count is the signal).
   * Below it, and against a backend that does not serve the count, the approved text still renders.
   */
  requiredDescription: REQUIRED_DESCRIPTION,
  /**
   * The L4 lock's exemption (constraint 6), its own sentence so one string serves all three places
   * that say who is asked: after the Required text, the zero case and the could-not-count line.
   * Drawn where the company's sign-in lock is on, and wherever this page cannot read the SSO mode
   * (`lockExemptionShown` in `settings-security-tab.tsx`): it is true at every tier.
   */
  lockExemption: 'Employees who sign in only through your identity provider aren’t asked.',

  // ── The confirmation, shown when an Owner saves Required ──────────────────────────────────────
  confirmTitle: 'Require two-factor authentication?',
  /** While the count is read. The L4 ceremony's own pending line, word for word. */
  confirmCounting: 'Checking who this would affect…',
  /**
   * Zero. A plain confirmation rather than none: Required still changes something for this company
   * when nobody would be asked now — linking from then on needs a second factor (D-AS-16 (4)).
   * Followed by `lockExemption` where the lock is on.
   */
  confirmNone:
    'Right now, no linked employees would be asked to add two-factor authentication. Employees who ' +
    'link their work email later would need it first.',
  /** Follows the one-employee line. Singular "they", as the count line uses. */
  confirmPersonalOne: 'Either way, their personal Givmo account stays theirs.',
  /** Follows the many-employees line, and the general line when the count could not be read. */
  confirmPersonalMany: 'Either way, their personal Givmo accounts stay theirs.',
  /**
   * Where the list will be, as PLAIN TEXT before the save. ⚠ "Once they're being asked" is
   * load-bearing: before Required is in force the roster reads `not_required` for everyone and
   * shows no two-factor column, so the list fills only then — the approved panel line's hedge
   * ("when they are, their status appears here and on the Employees page"), kept. No link here:
   * following one before Confirm would drop the unsaved choice for a list without the column.
   */
  confirmList: 'Once they’re being asked, their status appears on the Employees page.',

  // ── After Confirm has saved: the link to the list ─────────────────────────────────────────────
  /**
   * The same hedge, under the saved setting, now carrying the link — split round it so each part
   * is pinned. "employees", not "they": it stands in the card, after the heads-up has closed.
   */
  savedListBefore: 'Once employees are being asked, their status appears on the ',
  savedListLink: 'Employees page',
  savedListAfter: '.',
  /**
   * The count could not be read (a failed read, a body without it, or no answer within the bound).
   * The confirmation still says what Required does, without a number it does not have, and saving
   * stays possible: the count is a heads-up, not a gate. Followed by the personal line, then
   * `lockExemption` where the lock is on.
   */
  confirmGeneral:
    'Linked employees without two-factor authentication would be asked to add it or unlink your ' +
    'company.',
  confirmCountFailed: 'Could not check how many linked employees would be asked.',
  /** The approved panel's own retry word (`SECURITY_COPY.panelRetry`). */
  confirmCountRetry: 'Try again',
  confirm: 'Require two-factor authentication',
  cancel: 'Cancel',
} as const;

/**
 * The count line, for one employee or more. Zero is {@link TWO_FACTOR_REQUIREMENT_COPY}'s
 * `confirmNone`, a different sentence, so it is never spelled "0 linked employees".
 */
export function wouldBeAskedLine(count: number): string {
  const who =
    count === 1 ? '1 linked employee' : `${count.toLocaleString('en-US')} linked employees`;
  return `${who} would be asked to add two-factor authentication or unlink your company.`;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// Company sign-in (AS-WEB6 / AS-WEB7)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//
// ══ APPROVED, BUT FOR THE SIGN-IN LOCK AND DIRECTORY SYNC ═════════════════════════════════════
// Jonathan approved company sign-in's words on 2026-10-16 (D-AS-57, the company SSO copy sweep).
// They live in `SSO_COPY`, and a change to one is a RE-approval: `security-copy-freeze.test.tsx`
// pins each byte for byte. `SSO_COPY_DRAFT` keeps what that sweep left for its own rounds: the
// sign-in lock (its tier, its flip ceremony and the reset's identity-provider sentence) and
// directory sync. `sso-copy-freeze.test.tsx` pins those, so each sweep is a DELIBERATE edit with
// a failing test beside it, and none may reach an environment where its flag is on until approved.
//
// Both objects take the shape AS-WEB4's `SECURITY_COPY` takes: one const, pinned at the source
// rather than through rendered markup. The backend's setup guidance, which these screens render
// verbatim (rule 1 below), was approved in the same sweep and is pinned beside its constants
// (`tests/domain/test_employer_sso_config_entities.py`).
//
// ══ WHAT THE WORDING HAS TO CARRY ═════════════════════════════════════════════════════════════
// The audience for most of this is an IT ADMIN configuring an identity provider — not an end user
// and not a log — which is why the setup guidance reads as instructions with the failure named.
//
// Four constraints are load-bearing and a sweep must preserve them, not just the sense:
//
//   1. THE BACKEND OWNS THE SETUP GUIDANCE. `instructions`, `setup_guidance` and the
//      test-connection `guidance` are composed server-side and rendered VERBATIM. Nothing here
//      duplicates, paraphrases or overrides them — a second copy of a security instruction is a
//      second thing to keep in step, and the server's is the one that knows the company's own
//      record name and verified domain.
//   2. THE NAMEID FAILURE IS A SETUP INSTRUCTION, NEVER A SIGN-IN FAILURE. A test that never
//      comes back is overwhelmingly the Entra pairwise-identifier case (the broker refuses the
//      assertion at its own endpoint, so the browser never returns to us). Calling that
//      "sign-in failed" sends an admin to debug the wrong system for an afternoon.
//   3. THE L4 CEREMONY MAY NOT SOFTEN THE COST. It is the only screen in the product that can
//      lock a person out of their own account, and the escape hatch's price — the employment and
//      every employee-gated thing with it — is stated plainly rather than as reassurance.
//   4. NO CLAIM THIS SCREEN CANNOT SUBSTANTIATE. Same rail as the MFA section above it: this
//      surface states what the company ASKED FOR and what the server RETURNED. It never promises
//      that a tier is being enforced, never promises timing, and never reports a count it did not
//      receive.

/**
 * The tier vocabulary. Mirrors `CompanySsoMode` — see `sso-decode.ts`, whose LADDER order the
 * selector renders in, whatever order the copy is written in.
 */
export interface SsoTierCopy {
  mode: 'none' | 'employer_context' | 'signin_lock';
  label: string;
  description: string;
}

/** L0 and L3: APPROVED (D-AS-57). L4's words are `TIERS_DRAFT`, for the sign-in lock's own sweep. */
const TIERS: readonly SsoTierCopy[] = [
  {
    mode: 'none',
    label: 'Not used',
    description:
      'Employees sign in to Givmo the way they do today. Their work account is not involved.',
  },
  {
    mode: 'employer_context',
    label: 'Available for employees',
    description:
      'Employees can connect their work account and sign in with your company. To keep seeing ' +
      'what you share with employees and using company benefits, they sign in through your ' +
      'identity provider again from time to time. Their personal sign-in keeps working.',
  },
];

/** L4: DRAFT, for the sign-in lock's own sweep. */
const TIERS_DRAFT: readonly SsoTierCopy[] = [
  {
    mode: 'signin_lock',
    label: 'Required — company sign-in only',
    // ⚠ "EVERY employee linked to your company" is the load-bearing phrase, and the earlier draft
    // had it inverted. The backend puts the lock claim on every linked employee who is not an admin
    // of that company, CONNECTED WORK IDENTITY OR NOT (`employer_policy/lock_claims.py`: "A LINKED
    // EMPLOYEE WITH NO SSO IDENTITY STILL GETS THE CLAIM. Locking only the employees who have
    // completed the SSO ceremony would invert the tier"). So the employees with no work identity are
    // not the unaffected ones — they are the ones who cannot sign in at all, because they cannot
    // sign in to connect. Passkeys are in the list because the mint refuses them too
    // (`passkey_service._refuse_if_sso_locked`), which the blocking function alone would miss.
    description:
      'Every employee linked to your company — whether or not they have connected a work identity — ' +
      'can no longer sign in with a password, passkey, Google, Apple, Facebook, Microsoft or a ' +
      'phone number. They sign in through your identity provider instead. Employees who have not ' +
      'connected a work identity cannot sign in at all until they disconnect from your company. ' +
      'Read the summary before turning this on.',
  },
];

/**
 * Company sign-in's APPROVED strings: the few D-AS-39 took (copy round 3), everything the company
 * SSO copy sweep took (D-AS-57, 2026-10-16), and its round 2 (D-AS-84, 2026-10-24): the heading for
 * a test that didn't finish, what the identity provider said, and the roster's company sign-in
 * reset — each moved out of the draft object below as it was approved. A change is a RE-approval;
 * `security-copy-freeze.test.tsx` pins them. The sign-in lock's strings and directory sync's stay in
 * `SSO_COPY_DRAFT` for their own rounds.
 */
export const SSO_COPY = {
  // ── The section ──────────────────────────────────────────────────────────────────────────────
  sectionTitle: 'Company sign-in',
  sectionIntro:
    'Let employees sign in to Givmo with their work account. Set this up in order: verify your ' +
    'domain, connect your identity provider, test it, then choose what you ask of employees.',

  // ── Step 1 · domain verification ─────────────────────────────────────────────────────────────
  domainTitle: 'Step 1 · Verify your domain',
  domainSubtitle:
    'Proves you control the domain your employees’ work email is on, before it decides who can ' +
    'authenticate them.',
  /**
   * Rendered instead of the stepper when `company.domain` is not a domain we can query. Names no
   * host's page (D-AS-38 (2)): the portal's Account info and the console's Profile are different
   * places, and support's address is the same from both (D-AS-57 fix 6 names it).
   */
  domainUnusable:
    'Your company domain has to be a plain domain name, like example.com, before it can be ' +
    'verified. Email support@givmocharitable.org to have it corrected, then come back.',
  domainStart: 'Start verification',
  domainVerifiedTitle: 'Domain verified',
  domainRecordTypeLabel: 'Record type',
  domainRecordType: 'TXT',
  domainRecordNameLabel: 'Record name',
  domainRecordValueLabel: 'Record value',
  domainCheck: 'Check',
  domainChecking: 'Checking…',
  /** The re-mint affordance. It is destructive, so it says so before the click, not after. */
  domainRemint: 'Start over with a new record',
  domainRemintWarning:
    'Starting over issues a new record value and clears your current verification. The record you ' +
    'already published stops working, and any company sign-in that depends on this domain stops ' +
    'until the new one is verified.',
  domainRemintConfirm: 'Issue a new record',
  domainRemintCancel: 'Keep the current record',
  /** The copy-out block — Jonathan's 08-25 rider: hand it to a registrar, or to your own assistant. */
  domainHandoffTitle: 'Hand this to whoever manages your DNS',
  domainHandoffHelp:
    'This block is self-contained. Paste it to your domain registrar’s support, into your own AI ' +
    'assistant, or alongside your DNS provider’s API docs — it names everything needed to create ' +
    'the record.',
  domainHandoffCopy: 'Copy instructions',
  domainHandoffCopied: 'Instructions copied',
  domainHandoffCopyFailed: 'Could not copy. Select the text above and copy it manually.',
  /** Fallbacks for the two typed refusals whose own message we still prefer when present. */
  domainNotStarted: 'Start the verification again to get a fresh record to publish.',
  domainCheckFailed: 'Could not check the record just now. Please try again.',

  // ── Step 2 · the IdP connection ──────────────────────────────────────────────────────────────
  connectionTitle: 'Step 2 · Connect your identity provider',
  connectionSubtitle: 'Point Givmo at the identity provider your employees already sign in with.',
  connectionMetadataLabel: 'Metadata URL',
  connectionMetadataHelp:
    'The address of your identity provider’s SAML metadata document. Every major provider ' +
    'publishes one, and it is the option to prefer — it keeps working when your provider rotates ' +
    'its signing certificate.',
  connectionManualToggle: 'Enter the details by hand instead',
  connectionMetadataToggle: 'Use a metadata URL instead',
  connectionEntityIdLabel: 'Identity provider entity ID',
  connectionSignOnUrlLabel: 'Sign-on URL',
  connectionCertificateLabel: 'Signing certificate (PEM)',
  /** ONE sentence on why pasted XML is not an option — the plan asks for exactly one. */
  connectionXmlNote:
    'Pasted metadata XML is not accepted: that document decides which certificate Givmo trusts, so ' +
    'it is read from your provider’s own URL rather than from a paste.',
  connectionSave: 'Save connection',
  connectionSaved: 'Identity provider connected',
  connectionSaveFailed: 'Could not save the connection.',
  connectionConfiguredTitle: 'Paste these into your identity provider',
  relyingPartyIdLabel: 'Entity ID / audience',
  relyingPartyCallbackLabel: 'Reply URL (assertion consumer service)',
  /**
   * AS-WEB25 — the values are read from the identity-provider service, and that read can fail on its
   * own while the rest of the section stands. This leads EVERY such failure, in the title's place
   * (the title would promise values that are not there); the server's own sentence says why beneath
   * it (a typed `SSO_BROKER_UNAVAILABLE`: "We could not reach the single sign-on service…").
   */
  connectionValuesFailed: 'Could not load the values to paste into your identity provider.',
  connectionValuesRetry: 'Try again',
  connectionNeedsDomain: 'Verify your domain in Step 1 before connecting an identity provider.',
  /**
   * AS-WEB28 (D-AS-47, approved as drafted 2026-10-07, and in the copy sweep, D-AS-57) — Givmo's
   * values BEFORE the identity provider's details, in the order Okta's app wizard asks. The values
   * box's title before any values exist; once they do it carries `connectionConfiguredTitle`.
   */
  valuesTitle: 'Givmo’s values for your identity provider',
  /** Why the button comes first — and why it is optional for a provider that gives its metadata first. */
  valuesFirstHelp:
    'Okta and some other identity providers ask for these before they show you a metadata URL. ' +
    'Get them here first if yours does.',
  valuesGet: 'Get Givmo’s values',
  /** What the two big providers call the same two fields, so neither lands in the other's box. */
  valuesFieldNames:
    'Okta calls these the Audience URI (SP Entity ID) and the Single sign-on URL. Microsoft Entra ' +
    'calls them the Identifier (Entity ID) and the Reply URL.',
  valueCopy: 'Copy',
  valueCopied: 'Copied',
  /** The value stays on screen and selectable, so a refused clipboard is a missing convenience. */
  valueCopyFailed: 'Could not copy. Select the value and copy it by hand.',
  /** "Get Givmo's values" failed and the server sent no sentence of its own. */
  valuesGetFailed: 'Could not get the values.',
  /** The heading over the identity provider's details form. */
  idpTitle: 'Your identity provider’s details',
  /** Values on screen, details not yet saved: the setup is not finished. */
  idpNotYetConnected: 'Givmo connects to your identity provider once you save these.',
  /** The card's status once the details are saved (the save toast's words). */
  connectionConnected: 'Identity provider connected',
  /**
   * AS-WEB29 — the server says a connection is saved and the single sign-on service has nothing for
   * it (it lost the connection, or the organization). Saving again provisions a FRESH connection, so
   * the values change, which is why the next step names the identity provider too.
   */
  driftTitle: 'This connection needs to be set up again',
  driftBody:
    'Givmo’s single sign-on service no longer has this connection, so employees can’t sign in ' +
    'through your identity provider. Save your identity provider’s details below again, then ' +
    'replace the old values in your identity provider with the new ones that appear here.',

  // ── Step 3 · test connection ─────────────────────────────────────────────────────────────────
  testTitle: 'Step 3 · Test the connection',
  testSubtitle:
    'Signs you in through your identity provider for real, then reports what it sent back. ' +
    'Nothing is saved to your account and no employee is connected by a test.',
  testStart: 'Run a test sign-in',
  testRestart: 'Run another test',
  testWindowNote:
    'A new window opens for your identity provider. Finish signing in there, then come back to ' +
    'this page — the result appears here.',
  testPopupBlocked:
    'Your browser blocked the sign-in window. Allow pop-ups for this page and run the test again.',
  testCheckAgain: 'Check again',
  testSubjectLabel: 'Identifier your provider sent',
  testOkTitle: 'Test passed',
  testActionNeededTitle: 'Change one setting and test again',
  /**
   * The heading for a test that didn't finish (`status: "expired"`, D-AS-84 decision 3), with either
   * sign-in service: no answer came back, so "Change one setting" would claim a cause nobody saw. The
   * server's guidance under it says what to try.
   */
  testNoResultTitle: 'No result from this test',
  testWaitingTitle: 'Waiting for the sign-in to finish',
  testNeedsConnection: 'Connect your identity provider in Step 2 before testing.',
  testStartFailed: 'Could not start the test.',
  testReadFailed: 'Could not read the result of that test.',
  /**
   * `SSO_REDIRECT_URI_NOT_ALLOWED` — the honest environment answer, and the one refusal an operator
   * (not the employer) has to fix. It says what is missing without naming an internal flag or
   * telling an admin to do something they cannot do.
   */
  testRedirectNotRegistered:
    'Givmo hasn’t set up test sign-ins for this page yet, so the test can’t run here. Nothing is ' +
    'wrong with your configuration. If this keeps happening, email support@givmocharitable.org.',

  // ── Step 3 · what the identity provider said (AS-BE92; D-AS-84) ─────────────────────────────
  //
  // A test the identity provider refused (`idp_refused`) shows the server's guidance and, below it,
  // the identity provider's OWN words and status. ⚠ Those are unsigned text, which anyone holding the
  // test's broker id could have written, so they sit in their own block under this label, quoted
  // apart from our words, as plain text: escaped, capped (`IDP_MESSAGE_DISPLAY_LIMIT`,
  // `sso-decode.ts`) and never turned into a link. The label names their author (decision 5).
  testIdpSaid: 'Message your provider sent',
  testIdpStatusLabel: 'Status your provider sent',

  // ── Step 4 · the tier ────────────────────────────────────────────────────────────────────────
  tierTitle: 'Step 4 · Choose what employees sign in with',
  tierGroupLabel: 'Company sign-in requirement',
  tiers: TIERS,
  tierBlocked:
    'Finish Steps 1 and 2 first. Until your domain is verified and an identity provider is ' +
    'connected, company sign-in stays off.',
  /** Shown when a save came back holding a DIFFERENT tier from the one submitted (the server clamp). */
  tierClamped:
    'Company sign-in was saved as “Not used”. It needs a verified domain and a connected identity ' +
    'provider, and one of those is not in place right now.',
  freshnessLabel: 'Re-confirm with your identity provider every',
  freshnessNote:
    'How long an employee can see what you share with employees and use company benefits after ' +
    'signing in through your identity provider, before Givmo asks them to sign in with it again. ' +
    'It does not sign them out of Givmo.',
  save: 'Save',
  saved: 'Your company sign-in setting is saved',
  saveFailed: 'Could not save your company sign-in setting.',
  forbidden: 'Only company Owners can change these settings.',
  /** The section's own dark notice — the surface went dark between the probe and the click. */
  unavailable: 'Company sign-in settings aren’t available right now. Please try again later.',

  // ── Directory sync's one approved line ───────────────────────────────────────────────────────
  /**
   * ★ THE MATCH-REQUEST NOTE (AS-BE38 (web), plan (i); Jonathan's D-AS-16 addendum: every unlink
   * cancels the person's pending match requests, "both the employer and employee should see that").
   * A directory deprovision runs the same unlink as the employee's own (`employer_policy/unlink.py`
   * → `UserService.unlink_company`, CAM-R38) and has no confirmation to carry the line, so it stands
   * on the panel. It CONTINUES `scimSwitchHelp`, which it always follows: that line names the
   * person and the scope, so this one carries only the roster removal's approved consequence.
   */
  scimMatchRequestsNote: 'Their pending match requests are cancelled too.',

  // ── "Sign out every employee" (AS-BE19; AS-BE55 (web), D-AS-39 fix 5; D-AS-57) ───────────────
  //
  // The reset ends each signed-out employee's AI connections and partner app links too, and every AI
  // connected to the company (D-AS-32, D-AS-37; backend AS-BE55 with AS-BE65's per-person cut-off),
  // so the card says so — it never claims less than the reset does, nor more. The scope sentence is
  // still the whole control: what it REACHES (every linked employee, work account or not, and their
  // AI connections) and what it LEAVES ALONE (no factor or account touched, nobody removed). The
  // subtitle, scope and untouched line were approved by D-AS-39 fix 5, the card's other words by
  // D-AS-57. Its one sentence that needs a stored lock, `resetConfirmIdp`, is the lock's: it waits
  // in `SSO_COPY_DRAFT`.
  resetTitle: 'Sign out every employee',
  resetButton: 'Sign out all employees',
  resetSubtitle:
    'The blunt control for after an incident: end every employee’s Givmo session and AI ' +
    'connections at once, so they all have to sign in again.',
  resetConfirmTitle: 'Sign out every employee?',
  /** Who it reaches. Names the roster, because "linked employees" is not "employees using SSO". */
  resetConfirmScope:
    'Every employee linked to your company is signed out of Givmo straight away — whether or not ' +
    'they have connected a work account. The AI assistants and partner apps they’ve connected to ' +
    'Givmo lose access too, and so does every AI connected to your company. Company admins who are ' +
    'not linked as employees stay signed in.',
  /** What it does NOT do. Every clause here is a boundary the backend holds, not a comfort. */
  resetConfirmUntouched:
    'Nothing else changes. No one’s password, passkey or two-factor setup is touched, nobody is ' +
    'removed from your company, and no one loses access to their own account.',
  resetConfirm: 'Sign out all employees',
  resetCancel: 'Cancel',
  resetDoneTitle: 'Employees signed out',
  resetEmployeesLabel: 'Employees on your roster',
  resetRevokedLabel: 'Signed out',
  resetFailedLabel: 'Could not be signed out',
  /** The remedy, stated wherever the failure is. Repeating the act is safe, which is why it is the fix. */
  resetFailedRemedy:
    'Signing everyone out again is safe and will retry these. Employees who are already signed out ' +
    'stay signed out.',
  /** A 200 that carried no numbers. Says what is and is not known rather than showing a clean zero. */
  resetIncomplete:
    'Your employees were signed out, but Givmo could not read back how many. Sign everyone out ' +
    'again if you need a count you can rely on.',
  resetFailed: 'Could not sign your employees out.',

  // ── One employee's company sign-in reset (AS-BE96, D-AS-70 question 4; D-AS-84) ─────────────
  //
  // The Employees roster's row action for an Owner, offered only where the employee's company
  // sign-in is connected (`sso_linked`). The reset clears the connection and keeps the employment and
  // the directory pairing; the person connects again themselves, and is emailed. It can only remove
  // (question 5), so nothing here offers to choose an identity for anyone. The button names what it
  // resets, as the row action does (decision 10). Its two lock sentences are the sign-in lock's and
  // wait in `SSO_COPY_DRAFT` for that round.
  releaseAction: 'Reset company sign-in',
  releaseConfirmTitle: 'Reset this employee’s company sign-in?',
  releaseConfirmBody:
    'This disconnects their work account from Givmo. They stay on your employee list, and nothing ' +
    'else on their account changes. To use company sign-in again, they connect their work account ' +
    'from Settings in the Givmo app. We’ll email them to let them know.',
  releaseConfirm: 'Reset company sign-in',
  releaseDone: 'Company sign-in reset. We’ve emailed them.',
  // Review-80 F4: the toast says the employee was emailed only when the server says so.
  releaseDoneNotEmailed:
    'Company sign-in reset. We couldn’t email them, so let them know it was reset.',
  releaseDoneEmailUnknown: 'Company sign-in reset.',
  releaseNothing: 'Their work account was already disconnected, so there was nothing to reset.',
  releaseUnconfirmed: 'We couldn’t confirm the reset. Refresh the list to check.',
  releaseFailed: 'Could not reset their company sign-in. Please try again.',

  // ── The security audit export (AS-BE20) ──────────────────────────────────────────────────────
  auditTitle: 'Export your security history',
  auditSubtitle:
    'A record of the security changes on your company account — what changed, when, and whether an ' +
    'admin or an employee did it.',
  /** What is IN the file, said plainly, because the honest limit is also the reassuring one. */
  auditColumnsNote:
    'The file has four columns: when it happened, what happened, whether an admin or an employee ' +
    'did it, and one detail. It names no employee — no names, no email addresses, no identifiers.',
  auditFromLabel: 'From',
  auditToLabel: 'To',
  auditDownload: 'Download CSV',
  auditDownloading: 'Preparing your file…',
  /** The endpoint refuses an inverted range rather than swapping it, so the form says so first. */
  auditRangeInverted: 'The start of the range has to be on or before its end.',
  auditFailed: 'Could not export your security history.',
  /** Used only when the server sent no filename of its own. */
  auditFilename: 'company-security-audit.csv',

  // ── The test-connection landing page ─────────────────────────────────────────────────────────
  // "Security" names the place on both hosts — the portal's tab and the console's page (D-AS-38 (2):
  // the neutral wording, approved by D-AS-57).
  callbackTitle: 'Test sign-in finished',
  callbackBody: 'You can close this window and return to Security for the result.',
  callbackErrorTitle: 'That test sign-in did not finish',
  callbackErrorBody: 'Return to Security — the reason, and what to change, appears there.',
  callbackReturn: 'Return to Security',
} as const;

/**
 * Company sign-in's DRAFT strings: the sign-in lock's (L4), the roster reset's two lock sentences
 * among them, and directory sync's, left by the company SSO copy sweeps (D-AS-57, D-AS-84) for their
 * own rounds. `sso-copy-freeze.test.tsx` pins them.
 */
export const SSO_COPY_DRAFT = {
  // ── Step 4 · the sign-in lock's tier ─────────────────────────────────────────────────────────
  tiers: TIERS_DRAFT,

  // ── The L4 flip ceremony ─────────────────────────────────────────────────────────────────────
  lockTitle: 'Turn on company-only sign-in?',
  lockIntro:
    'Every employee linked to your company — whether or not they have connected a work identity — ' +
    'will no longer be able to sign in to Givmo with a password, passkey, Google, Apple, Facebook, ' +
    'Microsoft or a phone number. From then on they sign in through your identity provider. ' +
    'Employees who have not connected a work identity cannot sign in at all until they disconnect ' +
    'from your company.',
  lockReadinessTitle: 'What this does to your people right now',
  // ⚠ `linked_employees` is EMPLOYMENT, not the SSO ceremony. The earlier draft labelled it
  // "Employees who have connected their work identity" while labelling `without_sso_identity`
  // "Employees with no work identity connected" — one phrase for two different facts, in one panel,
  // where the second is a SUBSET of the first (`client_readiness_snapshot` counts it inside
  // `would_be_locked`).
  lockLinkedEmployees: 'Employees linked to your company',
  lockWouldBeLocked: 'Of those, employees who would be switched to company-only sign-in',
  // `exempt_admins` walks `company._employees` and counts the ones whose `admin_company` is this
  // company — i.e. admins who are ALSO linked employees. An admin who is not a linked employee is
  // never in that roster, never receives the claim, and is not counted here.
  lockExemptAdmins:
    'Company admins who are also linked employees (they keep their personal sign-in)',
  lockWithoutSsoIdentity:
    'Employees with no work identity connected — after the switch, their only way back in is to ' +
    'disconnect from your company',
  lockBelowClientFloor: 'Employees last seen on an app too old to guide them through this',
  lockClientVersionUnknown: 'Employees we have never seen an app version from',
  lockMinClientVersionLabel: 'Version checked against',
  /** `min_client_version: null` — the check DID NOT RUN, so a zero must not read as "everyone passed". */
  lockNoFloorNote:
    'No app version was checked in this environment, so the two app-version counts above are not ' +
    'evidence that everyone is on a current build.',
  /**
   * `exempt_admins === 0`. **A NOTE, NOT A WARNING** — the key keeps its name for continuity, but
   * the sentence and the rendering changed together.
   *
   * The earlier draft said "nobody at your company can sign in to turn this back off", which is
   * false in the ordinary shape: `exempt_admins` counts only admins who are ALSO linked employees,
   * and the usual Owner is not one. Such an admin is never in `company._employees`, so they never
   * receive the lock claim, are skipped by the passkey refusal, and keep every personal sign-in —
   * meaning the Owner reading that sentence could always have signed in. A zero here says something
   * narrower and far less alarming, and this says exactly that.
   */
  lockNoExemptAdminWarning:
    'None of your linked employees is a company admin. Company admins who are not linked as ' +
    'employees are not affected by this setting and can always sign in to turn it off.',
  lockBreakGlassNote:
    'Company admins keep their personal sign-in on purpose, so someone can always get in and turn ' +
    'this off if your identity provider goes down.',
  /** The escape hatch, stated with its price. Never as reassurance. */
  lockEscapeHatchNote:
    'An employee locked out of your identity provider can always disconnect their work identity ' +
    'themselves, from the sign-in screen, and get their personal account back. Doing that removes ' +
    'them from your company in Givmo: they lose company benefits and everything your company shares ' +
    'with employees, until they connect again.',
  lockAcknowledge: 'I have read what happens to employees who cannot use our identity provider.',
  lockConfirmPromptPrefix: 'Type your company name',
  lockConfirmPromptSuffix: 'to confirm.',
  lockConfirmLabel: 'Company name',
  lockConfirm: 'Turn on company-only sign-in',
  lockCancel: 'Cancel',
  lockReadinessFailed: 'Could not load who this would affect.',
  lockReadinessRetry: 'Try again',
  /** Rendered while the readiness read is in flight — the confirm stays unavailable until it lands. */
  lockReadinessPending: 'Checking who this would affect…',

  // ── The workforce session reset's lock sentence ──────────────────────────────────────────────
  /**
   * Rendered ONLY under a stored `signin_lock` (AS-WEB22): at L0 this is a plain sign-out, and at
   * `employer_context` the employees' personal sign-in keeps working, so the sentence would be false.
   */
  resetConfirmIdp:
    'Because company sign-in is turned on, they sign back in through your identity provider.',

  // ── The roster reset's lock sentences (AS-BE96) ──────────────────────────────────────────────
  //
  // The rest of one employee's company sign-in reset is approved (D-AS-84) and lives in `SSO_COPY`.
  // These two are the escape hatch's price, said before the Owner presses the reset: when the sign-in
  // lock binds the employee, signed out, their only way back in is "Lost access to your company
  // sign-in?" (its approved label, D-AS-57, quoted exactly), which removes them from the company.
  // Decided per person by the roster row's `sso_lock_active` (never a break-glass admin, review-80
  // F4): definite where it is true, conditional where the row doesn't say, and not at all where it is
  // false.
  releaseConfirmLock:
    'Your company requires company sign-in, so if they’re signed out of Givmo they’ll need to use ' +
    '“Lost access to your company sign-in?” on the sign-in screen. That removes them from your ' +
    'company on Givmo.',
  releaseConfirmLockMaybe:
    'If your company requires company sign-in and they’re signed out of Givmo, they’ll need to use ' +
    '“Lost access to your company sign-in?” on the sign-in screen, which removes them from your ' +
    'company on Givmo.',

  // ── Directory sync (SCIM) — AS-WEB7 ──────────────────────────────────────────────────────────
  //
  // ⚠ NOTHING HERE RESTATES THE SERVER'S GUIDANCE. `SCIM_SETUP_GUIDANCE`,
  // `SCIM_ALREADY_CONFIGURED_GUIDANCE` and `SCIM_DISABLED_GUIDANCE` are composed backend-side and
  // rendered verbatim (rule 1 above) — including the show-once warning that rides the minted token.
  // What this block adds is only what the server does not say: the labels, the controls, the
  // consequence an Owner needs BEFORE the switch moves, and one fallback for the show-once warning
  // in case the guidance does not arrive.
  scimTitle: 'Sync employees from your directory',
  scimSubtitle:
    'Keeps your Givmo employee list in step with your identity provider, so you are not adding and ' +
    'removing people twice. Separate from what you ask employees to sign in with — you can use ' +
    'either without the other.',
  scimSwitchLabel: 'Sync employees from my identity provider',
  /**
   * ★ THE CONSEQUENCE, STATED BEFORE THE SWITCH MOVES. Enabling arms a reconciliation that removes
   * employment rows, and an Owner reading only "keeps your list in step" would not know that. The
   * server's own guidance describes the state AFTER each write; this is the one sentence that has to
   * be readable before the first one.
   */
  scimSwitchHelp:
    'While this is on, an employee removed in your identity provider also loses their Givmo work ' +
    'profile and the company benefits that come with it.',
  /** Both preconditions are the same two the tier needs, so it names the steps rather than the fields. */
  scimNeedsSetup:
    'Verify your domain in Step 1 and connect your identity provider in Step 2 before turning on ' +
    'directory sync.',
  scimEnableFailed: 'Could not turn on directory sync.',
  scimDisableFailed: 'Could not turn off directory sync.',
  /** What every identity provider's provisioning screen calls this field. */
  scimBaseUrlLabel: 'SCIM endpoint URL',
  scimBaseUrlCopy: 'Copy URL',
  scimBaseUrlCopied: 'URL copied',
  scimTokenLabel: 'Access token',
  scimCopyFailed: 'Could not copy. Select the value above and copy it manually.',

  // ── The show-once token reveal ───────────────────────────────────────────────────────────────
  scimTokenRevealTitle: 'Copy your access token now',
  /**
   * Used ONLY when the server sent no `setup_guidance` of its own.
   *
   * ★ This is the one place a fallback is load-bearing rather than tidy. Everywhere else on this
   * screen a missing server sentence costs an explanation; here it would cost the ONLY warning
   * standing between an Owner and a credential they cannot get back, on the single render where the
   * value exists. So the sentence is duplicated deliberately, and a copy sweep must keep both.
   */
  scimTokenRevealFallback:
    'This is the only time this token is shown. Copy it into your identity provider before you ' +
    'leave this page — Givmo cannot show it again. If you lose it, generate a new one, which stops ' +
    'the old one working.',
  scimTokenCopy: 'Copy access token',
  scimTokenCopied: 'Access token copied',
  /** The label names what pressing it does, because it cannot be undone and there is no second look. */
  scimTokenDismiss: 'Done — hide the token',

  // ── Rotation ─────────────────────────────────────────────────────────────────────────────────
  scimRotate: 'Generate a new access token',
  /**
   * ⚠ DELIBERATELY SHORTER THAN IT WANTS TO BE. The obvious second sentence — "if you no longer
   * have yours, generate a new one" — is already the second half of the server's
   * `SCIM_ALREADY_CONFIGURED_GUIDANCE`, which renders directly above this whenever sync is on, and
   * the button below says it too. So this carries only the part nothing else does: that there is no
   * copy to go back to. Rule 1 of this module, applied where the duplication was actually visible on
   * screen rather than only in the source.
   */
  scimRotateHelp:
    'Givmo keeps no copy of your access token — it is shown once, when it is generated.',
  scimRotateConfirmTitle: 'Generate a new access token?',
  /** Says what breaks and for how long, because the Owner is the only person who can end it. */
  scimRotateConfirmBody:
    'Your current token stops working straight away. Your identity provider keeps trying to sync ' +
    'with it and keeps failing until you paste the new token into your provisioning settings.',
  scimRotateConfirm: 'Generate a new token',
  scimRotateCancel: 'Cancel',
  scimRotateFailed: 'Could not generate a new access token.',
  /** The SCIM half's own dark notice — a different sub-surface from the section's, and a different flag. */
  scimUnavailable: 'Directory sync is not available on this environment yet.',
} as const;

/**
 * AS-BE96 — the roster's reset row action, named for its employee (its accessible name; the button
 * shows only the icon). APPROVED (D-AS-84), pinned in `security-copy-freeze.test.tsx` beside
 * `SSO_COPY`'s reset strings.
 */
export function releaseActionLabel(employeeName: string): string {
  return `${SSO_COPY.releaseAction} for ${employeeName}`;
}

/**
 * The clamp notice, naming the day the export ACTUALLY starts at.
 *
 * ★ THE SILENT CLAMP IS THE FAILURE THIS PROGRAM BANS, and a clamp on a read is the same dishonesty
 * as a clamp on a write: the Owner asked for a period, got a shorter one, and would otherwise read
 * the file as the whole of it. So the served start is stated rather than merely the rule. It is only
 * ever rendered on an explicit report from the server — never inferred, and never rendered when the
 * report could not be read (see `auditWindowWasClamped`, which is tri-state for exactly that).
 *
 * APPROVED (D-AS-57), and pinned byte-for-byte.
 */
export function auditClampNotice(servedFrom: string, retentionDays: number | null): string {
  const bound =
    retentionDays === null
      ? 'Givmo keeps this history for a limited time.'
      : `Givmo keeps this history for ${retentionDays} days.`;
  return (
    `${bound} Your file starts on ${servedFrom}, which is as far back as the record goes — ` +
    'not the earlier date you chose.'
  );
}

/**
 * The retention statement for a range that was NOT clamped.
 *
 * Separate from the clamp notice on purpose: this one states the bound, that one states the bound
 * AND that it bit. Rendered only on a retention figure the server actually sent — with the header
 * unreadable the screen states no bound at all rather than a remembered one.
 */
export function auditRetentionNote(retentionDays: number): string {
  return `Givmo keeps this history for ${retentionDays} days.`;
}

/**
 * The self-contained DNS instruction block (Jonathan's 08-25 rider): something an admin can hand
 * to a registrar's support desk, paste into their own AI assistant, or read next to their DNS
 * provider's API docs, with nothing else from this screen needed.
 *
 * ⚠ It is COMPOSED here rather than taken from the backend's `instructions`, and the two are not
 * duplicates: the server's string is the on-screen next step ("add this, then select Check"), while
 * this is a standalone work order for a third party. Both are rendered — the server's above the
 * block, this one inside it — so the screen never paraphrases the server's own guidance.
 *
 * APPROVED (D-AS-57), and pinned byte-for-byte.
 */
export function domainInstructionBlock(recordName: string, token: string): string {
  return [
    'Please create the following DNS record so that Givmo can verify we control this domain.',
    '',
    `Record type: TXT`,
    `Record name: ${recordName}`,
    `Record value: ${token}`,
    'TTL: your provider’s default is fine.',
    '',
    'Notes:',
    '- The value must be published exactly as written above, with nothing added around it.',
    '- If your DNS provider asks for the name relative to the domain, drop the domain from the end.',
    '- Leave any other TXT records on the domain in place; this one is additional.',
    '- DNS changes can take up to an hour to appear.',
  ].join('\n');
}
